import { type Control, useController, type UseFormGetValues, type UseFormSetValue } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldLabel } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { extractCaptureDate } from '../../../../lib/exif'
import { cn } from '../../../../lib/utils'
import type { ClaimPhotoAttachment } from '../../../../models/claim'
import type { Step1FormValues } from './claimFormValues'
import { ClaimPhotoUploader } from './ClaimPhotoUploader'

interface ClaimPhotoSectionProps {
  control: Control<Step1FormValues>
  setValue: UseFormSetValue<Step1FormValues>
  getValues: UseFormGetValues<Step1FormValues>
  /** Files already on the claim (edit mode) — count toward the "at least one" rule. */
  existingPhotos?: ClaimPhotoAttachment[]
}

export function ClaimPhotoSection({
  control,
  setValue,
  getValues,
  existingPhotos = [],
}: ClaimPhotoSectionProps) {
  const { field: photos, fieldState: photosState } = useController({
    control,
    name: 'photos',
    rules: {
      validate: (v, values) =>
        values.photoDeferred ||
        v.length > 0 ||
        existingPhotos.length > 0 ||
        'Add a photo or document, or use Skip for now.',
    },
  })
  const { field: deferred } = useController({ control, name: 'photoDeferred' })
  const { field: reason } = useController({ control, name: 'deferralReason' })

  async function handleNewFiles(files: File[]) {
    // Files upload on submit; new ones only record when they were taken, from EXIF.
    if (getValues('capturedAt')) return
    try {
      const captured = await extractCaptureDate(files)
      if (captured && !getValues('capturedAt')) setValue('capturedAt', captured)
    } catch {
      // EXIF is best-effort — never block on it.
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload photos or documents</CardTitle>
        <CardDescription>
          We read whatever you add and fill in the rest of this claim from it, so there is less for you to
          type. No photos yet? Skip this and we&apos;ll follow up for them.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {deferred.value && (
          /* Deferred: show the reason. The photo UI below stays mounted (hidden) so
             the dropzone keeps the files the user already added. */
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-neutral-700">
              No photos for now. We&apos;ll follow up for them after you submit.
            </p>
            <Field>
              <FieldLabel htmlFor="claim-photo-deferral-reason" optional>
                Why no photos?
              </FieldLabel>
              <Textarea
                id="claim-photo-deferral-reason"
                placeholder="e.g. No signal at the scene, will upload later"
                value={reason.value}
                onChange={(e) => reason.onChange(e.target.value)}
              />
            </Field>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto self-start px-0 text-sm font-normal text-neutral-500 underline-offset-2 hover:text-neutral-700 hover:underline"
              onClick={() => deferred.onChange(false)}
            >
              ← I can upload after all
            </Button>
          </div>
        )}

        <div className={cn('flex flex-col gap-6', deferred.value && 'hidden')}>
          <ClaimPhotoUploader
            files={photos.value}
            onChange={photos.onChange}
            onNewFiles={handleNewFiles}
            error={photosState.error?.message}
            existingPhotos={existingPhotos}
          />
          <div className="border-t pt-4">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto px-0 text-sm font-normal text-neutral-500 underline-offset-2 hover:text-neutral-700 hover:underline"
              onClick={() => deferred.onChange(true)}
            >
              Skip for now →
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
